import { isReservationSectionBefore, nextReservationSectionId } from '@/wabunka-src/common/features/Reservation/core/accordion/sectionOrder';
import {
  FULFILLABLE_RESERVATION_SECTION_IDS,
  hasReservationSectionRequiredFields,
  type FulfillableReservationSectionId,
  type ReservationSectionId,
} from '@/wabunka-src/common/features/Reservation/core/requiredFields/sections';

export interface ReservationAccordionState {
  openSectionId: ReservationSectionId;
  /** 一度でも通過したセクション。開き直せるかどうかを決めるだけで、✓ は現在の充足から別に出す */
  visitedSectionIds: readonly ReservationSectionId[];
  /** Edit で寄り道する前に開いていたセクション。Continue はここへ戻す（Edit は巻き戻しではない） */
  resumeSectionId: ReservationSectionId | null;
}

export type ReservationSectionAutoCompletion = (sectionId: ReservationSectionId) => boolean;

/**
 * 着地した時点で既に埋まっているセクションは通過済みにして、その先から開く。
 * 3DS 失敗からフォームへ戻ったときに、復元済みのセクションを押し直さずに済む。
 * 空のフォームで着地したときは最初のセクションが未充足なので、そのまま S1 が開く。
 */
export const createReservationAccordionState = (
  isSectionFulfilled: (sectionId: FulfillableReservationSectionId) => boolean,
  canAutoComplete: ReservationSectionAutoCompletion,
): ReservationAccordionState => {
  const visitedSectionIds: ReservationSectionId[] = [];
  const isEverySectionFulfilled = FULFILLABLE_RESERVATION_SECTION_IDS.every((sectionId) => isSectionFulfilled(sectionId));

  for (const sectionId of FULFILLABLE_RESERVATION_SECTION_IDS) {
    if (!isSectionFulfilled(sectionId)) return { openSectionId: sectionId, visitedSectionIds, resumeSectionId: null };
    // 日程の変更から戻ったときに有料オプションを飛ばさないよう、全部埋まっている（3DS 復帰）とき以外は、自動完了できない必須の無いセクションで止める
    if (!isEverySectionFulfilled && !hasReservationSectionRequiredFields(sectionId) && !canAutoComplete(sectionId)) {
      return { openSectionId: sectionId, visitedSectionIds, resumeSectionId: null };
    }
    visitedSectionIds.push(sectionId);
  }

  return { openSectionId: 'payment', visitedSectionIds, resumeSectionId: null };
};

/** 予約ページを離れて戻ったときに、離れる前の開閉を引き継ぐ。開いていたセクションより前が欠けていれば引き継がない */
export const resumeReservationAccordionState = (state: ReservationAccordionState, isSectionFulfilled: (sectionId: FulfillableReservationSectionId) => boolean): ReservationAccordionState | null =>
  FULFILLABLE_RESERVATION_SECTION_IDS.filter((sectionId) => isReservationSectionBefore(sectionId, state.openSectionId)).every((sectionId) => isSectionFulfilled(sectionId)) ? state : null;

/**
 * セクションを開く。Edit で前のセクションへ戻るときと、送信時に未充足のセクションを見せるときの両方で使う。
 * 後ろから前へ開いたときだけ、それまでいたセクションを戻り先として覚える。
 */
export const openReservationSection = (state: ReservationAccordionState, sectionId: ReservationSectionId): ReservationAccordionState => {
  if (sectionId === state.openSectionId) return state;

  const resumeSectionId = state.resumeSectionId ?? (isReservationSectionBefore(sectionId, state.openSectionId) ? state.openSectionId : null);

  return { ...state, openSectionId: sectionId, resumeSectionId: resumeSectionId === sectionId ? null : resumeSectionId };
};

/**
 * セクションを完了にして次を開く。戻り先を覚えていればそこへ戻し、途中に自動完了できるセクションがあれば通過する。
 */
export const completeReservationSection = (state: ReservationAccordionState, sectionId: ReservationSectionId, canAutoComplete: ReservationSectionAutoCompletion): ReservationAccordionState => {
  const visitedSectionIds = new Set(state.visitedSectionIds);
  visitedSectionIds.add(sectionId);

  let openSectionId = state.resumeSectionId ?? nextReservationSectionId(sectionId) ?? sectionId;
  while (canAutoComplete(openSectionId)) {
    const next = nextReservationSectionId(openSectionId);
    if (next === null) break;
    visitedSectionIds.add(openSectionId);
    openSectionId = next;
  }

  return { openSectionId, visitedSectionIds: [...visitedSectionIds], resumeSectionId: null };
};
